import { useState, useEffect, useContext, CSSProperties } from "react";
import { Box, Button, Typography, Link } from "@mui/material";
import { FeedEntry, Medium } from "protogen/feed_pb";
import { EphemeralTask } from "../../types/tasks";
import { Suggestions } from "protogen/suggestions_pb";
import { ReactComponent as SparklesIcon } from "../../icons/Advisor/Sparkles.svg";
import {
  ChevronUpIcon,
  ChevronDownIcon,
  BoltIcon,
} from "@heroicons/react/24/outline";
import useIsMobile from "../hooks/useIsMobile";
import { EphemeralFact } from "../facts/types";
import { EphemeralEvent } from "../../types/calendars";
import TaskSuggestionItems from "./TaskSuggestionItems";
import EventSuggestionItems from "./EventSuggestionItems";
import FactSuggestionItem from "./FactSuggestionItem";
import useLocalStorageState from "../hooks/useLocalStorageState";
import SummarySuggestion from "./SummarySuggestion";
import FactSuggestionItems from "./FactSuggestionItems";
import { useGetAgentsThread } from "services/agents";
import { GetAgentsThreadRequest } from "protogen/agents_service_pb";
import DelegationConfirmDialog, {
  DelegationDialogData,
} from "components/delegation/DelegationConfirmDialog";
import { DelegationOpportunity } from "components/hooks/useTaskAgentState";
import { useCreateDelegationTask } from "services/delegation";
import { CreateDelegationTaskRequest } from "protogen/delegation_service_pb";
import { useCreateTask } from "services/tasks";
import { CreateTaskRequest } from "protogen/tasks_service_pb";
import { CurrentUserContext } from "components/context/RequireAuth";
import { parseDelegationOpportunityFromFunctionCall } from "types/delegation";

const SHOW_OPEN_SUGGESTION_MESSAGE_HISTORY_LIMIT = 5;

export const insertTaskSuggestion = (
  entries: FeedEntry[],
  actions: SuggestionActionsType,
  entryCreator: (entry: FeedEntry, index: number) => JSX.Element,
  sx?: CSSProperties,
  familyRef?: string,
): JSX.Element[] => {
  let lastTaskSuggestionTime = 0;

  return entries.flatMap((entry, idx) => {
    const result: JSX.Element[] = [entryCreator(entry, idx)];
    const ts = Number(entry.session);

    if (
      entry.medium === Medium.SMS &&
      lastTaskSuggestionTime !== ts &&
      entry.textMessage?.sessionSuggestions?.hasSuggestions
    ) {
      // set defaultOpen if ts is within the last 6 hours
      const isDefaultOpen =
        ts > Date.now() / 1000 - 6 * 60 * 60 &&
        idx < SHOW_OPEN_SUGGESTION_MESSAGE_HISTORY_LIMIT;
      result.unshift(
        <Suggestion
          actions={actions}
          suggestions={entry.textMessage.sessionSuggestions}
          entityRef={entry.ref}
          entityType={entry.medium === Medium.SMS ? "message" : "email"}
          key={`suggestion-${entry.medium}-${entry.ref}`}
          defaultOpen={isDefaultOpen}
          sx={sx}
          familyRef={familyRef}
          session={entry.session.toString()}
        />,
      );
      lastTaskSuggestionTime = ts;
    }

    return result;
  });
};

export type SuggestionActionsType = {
  suggestFact: (t: EphemeralFact) => void;
  suggestEvent: (t: EphemeralEvent) => void;
  suggestTask: (t: EphemeralTask) => void;
};

export const SuggestionsList = ({
  suggestions,
  entityRef,
  entityType,
  actions,
  familyRef,
  isOpen = true,
  disabled = false,
  inlineMode = false,
  hideSummary = false,
}: {
  suggestions: Suggestions;
  entityRef: string;
  entityType: string;
  actions: SuggestionActionsType;
  familyRef?: string;
  isOpen?: boolean;
  disabled?: boolean;
  inlineMode?: boolean;
  hideSummary?: boolean;
}) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={inlineMode ? "32px" : "12px"}
      width="100%"
    >
      {!hideSummary && suggestions.summary?.content && (
        <SummarySuggestion
          inlineMode={inlineMode}
          entityRef={entityRef}
          entityType={entityType}
          summary={suggestions.summary}
          isOpen={isOpen}
          disabled={disabled}
        />
      )}
      {suggestions.tasks.length > 0 && (
        <TaskSuggestionItems
          inlineMode={inlineMode}
          entityRef={entityRef}
          entityType={entityType}
          suggestions={suggestions.tasks}
          suggestTask={actions.suggestTask}
          isOpen={isOpen}
          disabled={disabled}
        />
      )}
      {suggestions.events.length > 0 && (
        <EventSuggestionItems
          inlineMode={inlineMode}
          entityRef={entityRef}
          entityType={entityType}
          suggestions={suggestions.events}
          suggestEvent={actions.suggestEvent}
          isOpen={isOpen}
          familyRef={familyRef}
          disabled={disabled}
        />
      )}
      {suggestions.facts.length > 0 &&
        (inlineMode ? (
          <FactSuggestionItems
            inlineMode={inlineMode}
            entityRef={entityRef}
            entityType={entityType}
            suggestions={suggestions.facts}
            suggestFact={actions.suggestFact}
            isOpen={isOpen}
            familyRef={familyRef || ""}
            disabled={disabled}
          />
        ) : (
          suggestions.facts.map((suggestion, index) => (
            <FactSuggestionItem
              inlineMode={inlineMode}
              key={index}
              entityRef={entityRef}
              entityType={entityType}
              suggestion={suggestion}
              suggestFact={actions.suggestFact}
              index={index}
              isOpen={isOpen}
              disabled={disabled}
            />
          ))
        ))}
    </Box>
  );
};

export const Suggestion = ({
  entityRef,
  entityType,
  suggestions,
  sx,
  actions,
  familyRef,
  disabled = false,
  defaultOpen = false,
  session = undefined,
  warningMessage,
}: {
  entityRef: string;
  entityType: string;
  suggestions: Suggestions;
  actions: SuggestionActionsType;
  familyRef?: string;
  sx?: CSSProperties;
  defaultOpen?: boolean;
  session?: string;
  disabled?: boolean;
  warningMessage?: string;
}) => {
  const isMobile = useIsMobile();
  const currentUser = useContext(CurrentUserContext);
  const [isOpen, setIsOpen] = useState(false);
  const [expanded, setExpanded] = useLocalStorageState<{
    [key: string]: boolean;
  }>("faye.settings.smart-suggestions-expanded", {});
  const [lightningDialogOpen, setLightningDialogOpen] = useState(false);
  const [delegationOpportunity, setDelegationOpportunity] =
    useState<DelegationOpportunity | null>(null);
  const [lightningTaskTitle, setLightningTaskTitle] = useState<string | null>(
    null,
  );

  const { request: getAgentsThread } = useGetAgentsThread();
  const { request: createDelegationTaskRequest } = useCreateDelegationTask();
  const [creatingDelegation, setCreatingDelegation] = useState(false);

  const { request: createTaskRequest } = useCreateTask();

  const handleLightningTaskConfirm = async (data: DelegationDialogData) => {
    if (!familyRef || !delegationOpportunity) {
      return;
    }

    setCreatingDelegation(true);

    const taskResponse = await createTaskRequest(
      new CreateTaskRequest({
        familyRef,
        assigneeRef: currentUser.ref,
        title: lightningTaskTitle || delegationOpportunity.title,
        priority: "medium",
        sourceEntityType: entityType,
        sourceEntityRef: entityRef,
        lightningAgentThreadRef: suggestions.lightningAgentThreadRef,
      }),
    );

    if (taskResponse?.task?.ref) {
      await createDelegationTaskRequest({
        title: delegationOpportunity.title,
        description: data.description,
        detailsJson: JSON.stringify(data.details),
        agentThreadRef: suggestions.lightningAgentThreadRef || "",
        taskRef: taskResponse.task.ref,
        functionCallId: delegationOpportunity.functionId,
        serviceType: delegationOpportunity.serviceType,
        actionDateSec: data.actionDateSec || 0,
        timeEstimateMinutes: data.timeEstimateMinutes || undefined,
      } as unknown as CreateDelegationTaskRequest);

      setCreatingDelegation(false);
      setLightningDialogOpen(false);

      // Find the agent block in the task to scroll to it
      const agentBlock = taskResponse.task.blocks.find(
        (block: { type: string; ref: string }) => block.type === "agent",
      );
      const scrollParam = agentBlock ? `?blockRef=${agentBlock.ref}` : "";
      window.location.href = `/tasks/${encodeURIComponent(
        taskResponse.task.ref,
      )}${scrollParam}`;
    }
  };

  useEffect(() => {
    if (defaultOpen) {
      setIsOpen(true);
    }
    if (session) {
      const isSessionOpen =
        expanded[session] === undefined ? defaultOpen : expanded[session];
      setIsOpen(isSessionOpen);
    }
  }, []);

  useEffect(() => {
    const fetchDelegationOpportunity = async () => {
      if (!suggestions.lightningAgentThreadRef) return;

      const response = await getAgentsThread(
        new GetAgentsThreadRequest({
          threadRef: suggestions.lightningAgentThreadRef,
          includeAdminMessageHistory: false,
        }),
      );

      if (response?.events) {
        let opportunity: DelegationOpportunity | null = null;

        for (const event of response.events) {
          const parsed = JSON.parse(event);

          if (parsed.eventType === "function_call") {
            const functionCallData = JSON.parse(parsed.content || "{}");
            const parsedOpportunity =
              parseDelegationOpportunityFromFunctionCall(functionCallData);
            if (parsedOpportunity) {
              opportunity = {
                ...parsedOpportunity,
                agentThreadRef: suggestions.lightningAgentThreadRef,
              };
            }
          }
        }

        if (opportunity) {
          setDelegationOpportunity(opportunity);
          setLightningTaskTitle(opportunity.title);
        }
      }
    };

    fetchDelegationOpportunity();
  }, [suggestions.lightningAgentThreadRef]);

  return (
    <Box
      sx={{
        marginBottom: "20px",
        width: "100%",
        maxWidth: isMobile ? "100%" : "450px",
        ...sx,
      }}
    >
      {suggestions.lightningAgentThreadRef && !suggestions.lightningTaskRef && (
        <Box
          sx={{
            marginBottom: "12px",
            backgroundColor: (theme) => theme.palette.teal[1],
            borderRadius: "8px",
            padding: "16px",
            opacity: disabled ? 0.5 : 1,
            border: (theme) => `1px solid ${theme.palette.teal[2]}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              width: "100%",
            }}
          >
            <BoltIcon
              height="20px"
              width="20px"
              fill="#198282"
              stroke="#198282"
              strokeWidth="2px"
              style={{ minWidth: "20px", marginTop: "2px" }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="bodySmallHeavy"
                sx={{
                  color: (theme) => theme.palette.primary.main,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  marginBottom: "4px",
                }}
              >
                {lightningTaskTitle || "Lightning task"}
              </Typography>
              <Typography
                variant="bodySmall"
                sx={{ color: "#6B6E7B", marginBottom: "12px" }}
              >
                Have another advisor complete this task,{" "}
                <Link
                  href="https://advisor.findfaye.com/documents/4194a19c-91a5-43f0-87fa-2889e662edf1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  learn more
                </Link>
              </Typography>
              <Box sx={{ display: "flex" }}>
                <Button
                  variant="contained"
                  disabled={disabled || !delegationOpportunity}
                  onClick={() => {
                    if (!disabled && delegationOpportunity) {
                      setLightningDialogOpen(true);
                    }
                  }}
                >
                  Send to Lightning queue
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            backgroundColor: isOpen ? "inherit" : "#FFF",
            padding: isOpen ? "0px" : "0px 16px",
            border: isOpen ? "none" : "1px solid #F2F2F2",
            borderRadius: "8px",
            cursor: "pointer",
            paddingLeft: isOpen ? "17px" : undefined,
          }}
          onClick={() => {
            if (session) {
              setExpanded((prevState) => {
                return {
                  ...prevState,
                  [session]: !isOpen,
                };
              });
            }
            setIsOpen(!isOpen);
          }}
        >
          <Button
            sx={{
              ".MuiButton-startIcon": {
                margin: 0,
                marginRight: "8px",
              },
            }}
            variant="text"
            startIcon={<SparklesIcon />}
          >
            <Typography color="text.secondary" variant="bodySmall">
              Smart suggestions
            </Typography>
          </Button>
          {isOpen ? (
            <ChevronUpIcon height="20px" width="20px" strokeWidth="2" />
          ) : (
            <ChevronDownIcon height="20px" width="20px" strokeWidth="2" />
          )}
        </Box>
      </Box>
      <SuggestionsList
        suggestions={suggestions}
        actions={actions}
        isOpen={isOpen}
        entityRef={entityRef}
        entityType={entityType}
        familyRef={familyRef}
        disabled={disabled}
      />
      {warningMessage && (
        <Typography
          variant="bodySmall"
          sx={{ opacity: 0.26, marginTop: "6px" }}
        >
          {warningMessage}
        </Typography>
      )}
      {delegationOpportunity && (
        <DelegationConfirmDialog
          open={lightningDialogOpen}
          task={delegationOpportunity}
          familyRef={familyRef}
          isLoading={creatingDelegation}
          onClose={() => {
            setLightningDialogOpen(false);
          }}
          onConfirm={handleLightningTaskConfirm}
        />
      )}
    </Box>
  );
};
